import { useCallback, useEffect, useRef, useState } from 'react';
import { supabase } from '../../lib/supabase';
import NewsletterEngagementPanel from './NewsletterEngagementPanel';

interface NewsletterReportTabProps {
  newsletterId: string;
  scheduledFor?: string | null;
}

interface SendCounts {
  sent: number;
  total: number;
  inFlight: number;
}

export default function NewsletterReportTab({
  newsletterId,
  scheduledFor = null,
}: NewsletterReportTabProps) {
  const [counts, setCounts] = useState<SendCounts>({ sent: 0, total: 0, inFlight: 0 });
  const [refreshKey, setRefreshKey] = useState(0);
  const [live, setLive] = useState(false);

  const loadCounts = useCallback(async () => {
    try {
      const { data, error } = await supabase
        .from('scheduled_emails')
        .select('status')
        .eq('newsletter_id', newsletterId);
      if (error) throw error;

      let sent = 0;
      let total = 0;
      let inFlight = 0;
      for (const row of (data ?? []) as { status: string }[]) {
        total += 1;
        if (row.status === 'sent') sent += 1;
        if (row.status === 'pending' || row.status === 'sending') inFlight += 1;
      }
      setCounts({ sent, total, inFlight });
    } catch (e) {
      console.warn('NewsletterReportTab loadCounts:', e instanceof Error ? e.message : e);
    }
  }, [newsletterId]);

  useEffect(() => {
    void loadCounts();
  }, [loadCounts, refreshKey]);

  const refresh = useCallback(() => {
    void loadCounts();
    setRefreshKey((k) => k + 1);
  }, [loadCounts]);

  const refreshRef = useRef(refresh);
  useEffect(() => {
    refreshRef.current = refresh;
  }, [refresh]);

  useEffect(() => {
    setLive(false);
    const channel = supabase
      .channel(`scheduled_emails:report:${newsletterId}`)
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'scheduled_emails',
          filter: `newsletter_id=eq.${newsletterId}`,
        },
        () => {
          refreshRef.current();
        },
      )
      .subscribe((status) => {
        setLive(status === 'SUBSCRIBED');
      });

    return () => {
      void supabase.removeChannel(channel);
    };
  }, [newsletterId]);

  useEffect(() => {
    if (counts.inFlight === 0 && counts.sent === 0) return;

    const tick = () => {
      if (typeof document !== 'undefined' && document.hidden) return;
      refreshRef.current();
    };

    const intervalMs =
      counts.inFlight > 0 ? (live ? 15_000 : 8_000) : live ? 45_000 : 30_000;
    const handle = window.setInterval(tick, intervalMs);
    const onVisibility = () => {
      if (!document.hidden) tick();
    };
    document.addEventListener('visibilitychange', onVisibility);

    return () => {
      window.clearInterval(handle);
      document.removeEventListener('visibilitychange', onVisibility);
    };
  }, [newsletterId, counts.inFlight, counts.sent, live]);

  return (
    <NewsletterEngagementPanel
      newsletterId={newsletterId}
      recipientsSent={counts.sent}
      recipientsTotal={counts.total}
      scheduledFor={scheduledFor}
      refreshKey={refreshKey}
    />
  );
}
